import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { LaborOrder } from 'src/app/core/interfaces/labor-order.interface';
import { NotificationService } from 'src/app/core/services/notification.service';
import { OrdersService } from 'src/app/features/orders/services/orders.service';
import { DialogFooterComponent } from 'src/app/shared/components/dialog-footer/dialog-footer.component';
import { TextInputComponent } from 'src/app/shared/components/text-input/text-input.component';
import { MomentFormatPipe } from 'src/app/shared/pipes/moment-format.pipe-standalone';
import { DuplicateMultipleTimesheets, LoadTimeSheetsGroupedByLaborOrder } from '../../states/timesheets/timesheets.actions';

@Component({
    selector: 'app-modal-select-labor-order-duplicate',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatProgressSpinnerModule,
        MomentFormatPipe,
        TextInputComponent,
        DialogFooterComponent
    ],
    templateUrl: './modal-select-labor-order-duplicate.component.html',
    styles: [`
    .base-input-label {
      margin-right: 0!important;
    }`
    ]
})
export class ModalSelectLaborOrderDuplicateComponent implements OnInit, OnDestroy {

  searchOrderControl: UntypedFormControl;
  isSaving: boolean;
  isLoading: boolean;
  selectedLaborOrder: LaborOrder;
  laborOrders: Array<LaborOrder>;
  private unsubscribe$: Subject<void>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      timesheetIds: string[],
      currentLaborOrder: LaborOrder
    },
    private store: Store,
    private dialogRef: MatDialogRef<ModalSelectLaborOrderDuplicateComponent>,
    private _ordersService: OrdersService,
    private _notificationService: NotificationService
  ) {
    this.unsubscribe$ = new Subject<void>();
  }

  ngOnInit(): void {
    this.searchOrderControl = new UntypedFormControl('');
    this.subscribeSearchControl();
  }

  private subscribeSearchControl(): void {
    this.searchOrderControl.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(500),
    ).subscribe(
      (value) => {
        this.isLoading = true;
        if (value && value.length >= 3) {
          this.searchLaborOrders(value);
        } else {
          this.laborOrders = null;
          this.isLoading = false;
        }
      }
    );
  }

  private searchLaborOrders(value: string): void {
    this._ordersService.getOrders(0, 150, { searchQ: value }).toPromise().then(
      (response) => {
        this.laborOrders = response.items;
        this.isLoading = false;
      }
    )
  }

  duplicate(): void {
    this.isSaving = true;
    this.dialogRef.disableClose = true;
    this.store.dispatch(new DuplicateMultipleTimesheets(
      this.data.timesheetIds,
      this.selectedLaborOrder.id,
      (response) => {
        if (response) {
          this._notificationService.showError(response?.error?.error?.message || "There was a problem duplicating timesheets. Please try again.");
          this.isSaving = false;
        } else {
          this._notificationService.showSuccess("The timesheets were successfully duplicated.");
          // Refresh the timesheets list
          this.store.dispatch(new LoadTimeSheetsGroupedByLaborOrder(0, 50));
          this.close();
        }
      },
      (error) => {
        this._notificationService.showError("There was a problem duplicating timesheets. Please try again.");
        this.isSaving = false;
      }
    ));
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
